/**
 * Role Play Template Preview Screen
 *
 * Displays the full details of a premade template: setup overview,
 * vocabulary, and key phrases. A "Start this Role Play" button at the
 * bottom creates a slot directly from the template.
 *
 * @see docs/specs/mobile/roleplay.mobile.md (ROLEPLAY-004)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { SectionCard } from '../components/ui/SectionCard';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useRolePlayStore,
  selectRolePlayTemplates,
  selectRolePlayTemplatesLoading,
  selectRolePlayTemplateErrorInfo,
  selectRolePlayTemplateStartErrorInfo,
  selectRolePlayMutating,
  selectLoadTemplateDetail,
  selectStartFromTemplate,
  selectClearTemplateError,
} from '../store/rolePlayStore';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../types/lesson';
import type { RolePlayCatalogVocab, RolePlayCatalogExpression } from '../store/rolePlayStore';
import { StatusChip } from '../components/roleplay/StatusChip';

// ── Field row ─────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  value: string;
}

const Field = React.memo(function Field({ label, value }: FieldProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <View style={{ marginBottom: 12 }}>
      <Text
        allowFontScaling={false}
        style={{ fontSize: 12 * fontScale, color: theme.textSecondary, fontWeight: '600', marginBottom: 2, ...ffs }}
      >
        {label}
      </Text>
      <Text
        allowFontScaling={false}
        style={{ fontSize: 15 * fontScale, color: theme.text, ...ffs }}
      >
        {value}
      </Text>
    </View>
  );
});

// ── Screen ────────────────────────────────────────────────────────

export function RolePlayTemplatePreviewScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const templateId = (route.params as { templateId: string }).templateId;

  const templates = useRolePlayStore(selectRolePlayTemplates);
  const template = useMemo(() => templates.find((t2) => t2.id === templateId) ?? null, [templates, templateId]);
  const templatesLoading = useRolePlayStore(selectRolePlayTemplatesLoading);
  const templateDetailError = useRolePlayStore(selectRolePlayTemplateErrorInfo);
  const templateStartError = useRolePlayStore(selectRolePlayTemplateStartErrorInfo);
  // Surface a start failure preferentially — it's the user's last action and
  // must persist even if a background detail re-fetch succeeds (and clears the
  // detail error).
  const templateError = templateStartError ?? templateDetailError;
  const mutating = useRolePlayStore(selectRolePlayMutating);
  const loadTemplateDetail = useRolePlayStore(selectLoadTemplateDetail);
  const startFromTemplate = useRolePlayStore(selectStartFromTemplate);
  const clearTemplateError = useRolePlayStore(selectClearTemplateError);

  // Locale resolution for bilingual content
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  const resolve = useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale),
    [lessonLanguage, sourceLocale, targetLocale],
  );
  // Vocabulary/phrases: term/phrase → always target, translation → always source, context/note → lessonLanguage
  const resolveSource = useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, 'source', sourceLocale, targetLocale),
    [sourceLocale, targetLocale],
  );
  const resolveTarget = useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, 'target', sourceLocale, targetLocale),
    [sourceLocale, targetLocale],
  );
  // Catalog rows print their HEADWORD, which is always the American form. The
  // British rendering lives in `translation.en_GB` on the same row, so resolve
  // it and fall back to the headword when a row has no usable translation.
  const resolveTerm = useCallback(
    (term: string, translation?: BilingualContent | null) =>
      translation
        ? resolveBilingualContent(translation, 'target', sourceLocale, targetLocale) || term
        : term,
    [sourceLocale, targetLocale],
  );

  // Initial detail load — fires once when the screen mounts and the
  // template hasn't been hydrated yet (no `scenarioDetails`).
  useEffect(() => {
    if (template && !template.scenarioDetails) {
      void loadTemplateDetail(templateId);
    }
  }, [template, templateId, loadTemplateDetail]);

  // Re-fetch on focus so a user who chats to a new mastery state and
  // navigates back sees fresh status chips. The initial mount also
  // triggers focus, so this would double-fetch on cold start without
  // the `scenarioDetails` guard above; in steady state the focus
  // refetch is the only mastery-refresh path.
  useFocusEffect(
    useCallback(() => {
      // Don't fetch if we don't have a template entry yet — the cold-
      // start `useEffect` above handles that.
      if (template && template.scenarioDetails) {
        void loadTemplateDetail(templateId);
      }
    }, [template, templateId, loadTemplateDetail]),
  );

  // Pull-to-refresh: explicit user-driven refetch. We surface a separate
  // refreshing state so the spinner only shows on the user's gesture,
  // not on background focus refetches.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadTemplateDetail(templateId);
    } finally {
      setRefreshing(false);
    }
  }, [loadTemplateDetail, templateId]);

  const handleStart = useCallback(async () => {
    clearTemplateError();
    const conversationId = await startFromTemplate(templateId);
    if (conversationId) {
      navigation.navigate('Chat', { sessionId: conversationId });
    }
  }, [templateId, startFromTemplate, clearTemplateError, navigation]);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const isLoading = templatesLoading || (template && !template.scenarioDetails && !templateError);

  return (
    <View testID="roleplay-template-preview-screen" style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('roleplay.templates.preview.title')} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        {isLoading ? (
          <View style={{ paddingVertical: 40 }}>
            <ActivityIndicator size="small" color={theme.primary} />
          </View>
        ) : !template ? (
          /* 404 or template not in store — show error */
          <View
            testID="roleplay-template-preview-error-banner"
            style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
          >
            <Text
              allowFontScaling={false}
              style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
            >
              {t('errors.api.ROLEPLAY_TEMPLATE_NOT_FOUND')}
            </Text>
          </View>
        ) : (
          <>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              refreshControl={
                <RefreshControl
                  testID="roleplay-template-preview-refresh"
                  refreshing={refreshing}
                  onRefresh={handleRefresh}
                  tintColor={theme.primary}
                />
              }
            >
              {/* Error banner */}
              {templateError && (
                <View
                  testID="roleplay-template-preview-error-banner"
                  style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
                  >
                    {errorMessage(templateError)}
                  </Text>
                </View>
              )}

              {/* Setup section */}
              <SectionCard testID="roleplay-template-preview-setup">
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: 18 * fontScale,
                    fontWeight: '700',
                    color: theme.text,
                    marginBottom: 16,
                    ...ffs,
                  }}
                >
                  {resolve(template.title)}
                </Text>
                <Field label={t('roleplay.detail.fieldScenario')} value={resolve(template.scenario)} />
                {template.scenarioDetails && (
                  <Field label={t('roleplay.detail.fieldDetails')} value={resolve(template.scenarioDetails)} />
                )}
                <Field label={t('roleplay.detail.fieldUserRole')} value={resolve(template.userRole)} />
                <Field label={t('roleplay.detail.fieldAiRole')} value={resolve(template.aiRole)} />
                <Field
                  label={t('roleplay.detail.fieldFirstSpeaker')}
                  value={template.firstSpeaker === 'user'
                    ? t('roleplay.detail.firstSpeakerUser')
                    : t('roleplay.detail.firstSpeakerAi')}
                />
              </SectionCard>

              {/* Vocabulary section — Phase 4 prefers relation-backed
                  `catalogVocab` (carries learner status + per-template
                  importance). Falls back to legacy embedded `vocabulary`
                  for templates that haven't been migrated yet. */}
              {template.catalogVocab && template.catalogVocab.length > 0 ? (
                <SectionCard testID="roleplay-template-preview-vocabulary">
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: 16 * fontScale,
                      fontWeight: '700',
                      color: theme.text,
                      marginBottom: 12,
                      ...ffs,
                    }}
                  >
                    {t('roleplay.templates.preview.sectionVocabulary')}
                  </Text>
                  {template.catalogVocab.map((item: RolePlayCatalogVocab, idx) => (
                    <View
                      key={item.id}
                      testID={`roleplay-template-preview-vocab-${idx}`}
                      style={{ marginBottom: idx < template.catalogVocab!.length - 1 ? 12 : 0 }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 15 * fontScale, fontWeight: '600', color: theme.text, flexShrink: 1, ...ffs }}
                        >
                          {resolveTerm(item.lemma, item.translation)}
                        </Text>
                        <StatusChip status={item.status} />
                      </View>
                      <Text
                        allowFontScaling={false}
                        style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
                      >
                        {resolveSource(item.translation)}
                      </Text>
                      {item.contextNote && (
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, fontStyle: 'italic', ...ffs }}
                        >
                          {resolve(item.contextNote)}
                        </Text>
                      )}
                    </View>
                  ))}
                </SectionCard>
              ) : (
                template.vocabulary && template.vocabulary.length > 0 && (
                  <SectionCard testID="roleplay-template-preview-vocabulary">
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: 16 * fontScale,
                        fontWeight: '700',
                        color: theme.text,
                        marginBottom: 12,
                        ...ffs,
                      }}
                    >
                      {t('roleplay.templates.preview.sectionVocabulary')}
                    </Text>
                    {template.vocabulary.map((item, idx) => (
                      <View
                        key={idx}
                        testID={`roleplay-template-preview-vocab-${idx}`}
                        style={{ marginBottom: idx < template.vocabulary!.length - 1 ? 12 : 0 }}
                      >
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 15 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
                        >
                          {resolveTarget(item.term)}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
                        >
                          {resolveSource(item.translation)}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, fontStyle: 'italic', ...ffs }}
                        >
                          {resolve(item.context)}
                        </Text>
                      </View>
                    ))}
                  </SectionCard>
                )
              )}

              {/* Phrases section — same Phase 4 fallback strategy as
                  vocabulary above. */}
              {template.catalogExpressions && template.catalogExpressions.length > 0 ? (
                <SectionCard testID="roleplay-template-preview-phrases">
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: 16 * fontScale,
                      fontWeight: '700',
                      color: theme.text,
                      marginBottom: 12,
                      ...ffs,
                    }}
                  >
                    {t('roleplay.templates.preview.sectionPhrases')}
                  </Text>
                  {template.catalogExpressions.map((item: RolePlayCatalogExpression, idx) => (
                    <View
                      key={item.id}
                      testID={`roleplay-template-preview-phrase-${idx}`}
                      style={{ marginBottom: idx < template.catalogExpressions!.length - 1 ? 12 : 0 }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 15 * fontScale, fontWeight: '600', color: theme.text, flexShrink: 1, ...ffs }}
                        >
                          {resolveTerm(item.text, item.translation)}
                        </Text>
                        <StatusChip status={item.status} />
                      </View>
                      <Text
                        allowFontScaling={false}
                        style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
                      >
                        {resolveSource(item.translation)}
                      </Text>
                      {item.contextNote && (
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, fontStyle: 'italic', ...ffs }}
                        >
                          {resolve(item.contextNote)}
                        </Text>
                      )}
                    </View>
                  ))}
                </SectionCard>
              ) : (
                template.phrases && template.phrases.length > 0 && (
                  <SectionCard testID="roleplay-template-preview-phrases">
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: 16 * fontScale,
                        fontWeight: '700',
                        color: theme.text,
                        marginBottom: 12,
                        ...ffs,
                      }}
                    >
                      {t('roleplay.templates.preview.sectionPhrases')}
                    </Text>
                    {template.phrases.map((item, idx) => (
                      <View
                        key={idx}
                        testID={`roleplay-template-preview-phrase-${idx}`}
                        style={{ marginBottom: idx < template.phrases!.length - 1 ? 12 : 0 }}
                      >
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 15 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
                        >
                          {resolveTarget(item.phrase)}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
                        >
                          {resolveSource(item.translation)}
                        </Text>
                        <Text
                          allowFontScaling={false}
                          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, fontStyle: 'italic', ...ffs }}
                        >
                          {resolve(item.note)}
                        </Text>
                      </View>
                    ))}
                  </SectionCard>
                )
              )}

              {/* Spacer for fixed bottom button */}
              <View style={{ height: 80 }} />
            </ScrollView>

            {/* Fixed bottom "Start" button */}
            <View style={[styles.bottomBar, { backgroundColor: theme.background, borderTopColor: theme.border }]}>
              <TouchableOpacity
                testID="roleplay-template-preview-start"
                accessibilityRole="button"
                accessibilityLabel={t('roleplay.templates.preview.startButton')}
                disabled={mutating}
                onPress={handleStart}
                activeOpacity={0.75}
                style={[
                  styles.startButton,
                  { backgroundColor: theme.primary, opacity: mutating ? 0.6 : 1 },
                ]}
              >
                {mutating ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: 16 * fontScale,
                      fontWeight: '700',
                      color: '#fff',
                      textAlign: 'center',
                      ...ffs,
                    }}
                  >
                    {t('roleplay.templates.preview.startButton')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 4,
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    borderTopWidth: 1,
  },
  startButton: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
